/**
 * Presenter drawer — the control surface for the home health demo.
 *
 * Script screens, the script's events grouped by act as buttons that happen now,
 * Reset database, store reset, event log. The whole run is drivable from here.
 * There is deliberately no clock control: the demo never time-travels — later
 * stages are other patients.
 */
import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  ClickAwayListener,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useBranchClock } from "../components/branchClock";
import { seedDayStanding, selectDemoNow } from "@caredna-marlin/utils/homeHealthDemo/engine/clock";
import { setHomeHealthDemoEnabled } from "../demoEnabled";
import { useDemoDispatch, useDemoState } from "../store/useDemoStore";
import * as actions from "./actions";
import { EventButtons } from "./EventButtons";
import { HelpTip } from "./HelpTip";
import { LastResetItem } from "./LastReset";
import { ResetDemoControl } from "./ResetDemoControl";
import { ScriptNav } from "./ScriptNav";

export const PRESENTER_DRAWER_WIDTH = 380;

/** Marks the app-bar button that toggles the drawer; a click on it is not a click away. */
export const PRESENTER_TOGGLE_ATTR = "data-presenter-toggle";

export interface PresenterDrawerProps {
  open: boolean;
  onClose: () => void;
  onDisabled?: () => void;
}

function useTick(active: boolean, ms = 1000): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    if (!active) return;
    setNow(new Date());
    const id = setInterval(() => setNow(new Date()), ms);
    return () => clearInterval(id);
  }, [active, ms]);
  return now;
}

export function PresenterDrawer({ open, onClose, onDisabled }: PresenterDrawerProps) {
  const state = useDemoState();
  const dispatch = useDemoDispatch();
  const wallNow = useTick(open);
  const [confirmReset, setConfirmReset] = useState(false);

  const tz = state.config.branch.timeZone;
  const clock = useBranchClock(tz);
  const demoNow = selectDemoNow(state, wallNow);
  // A seed built for tomorrow reads as tomorrow rather than as stale.
  const standing = seedDayStanding(state, wallNow);

  const onReset = () => {
    if (!confirmReset) {
      setConfirmReset(true);
      return;
    }
    actions.resetStore();
    setConfirmReset(false);
  };
  const status = actions.selectPresenterStatus(state);

  // A click anywhere outside the drawer hides it (Pete, 2026-10-12). Clicks in
  // its own popups — the date picker, tooltips — count as inside: they are in
  // its React tree. The toggle handles its own click, so it is ignored here.
  const onClickAway = (event: MouseEvent | TouchEvent) => {
    if (event.target instanceof Element && event.target.closest(`[${PRESENTER_TOGGLE_ATTR}]`)) return;
    onClose();
  };

  const onDisable = () => {
    setHomeHealthDemoEnabled(false);
    onClose();
    onDisabled?.();
  };

  return (
    <ClickAwayListener onClickAway={onClickAway} mouseEvent={open ? "onClick" : false} touchEvent={open ? "onTouchEnd" : false}>
      <Drawer anchor="right" variant="persistent" open={open} PaperProps={{ sx: { width: PRESENTER_DRAWER_WIDTH, pt: 8 } }}>
        <Box sx={{ px: 2, pb: 2, display: "flex", flexDirection: "column", gap: 2, overflowY: "auto" }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Typography variant="h6">Presenter</Typography>
            <IconButton onClick={onClose} aria-label="Close presenter">
              <CloseIcon />
            </IconButton>
          </Box>

          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Status
            </Typography>
            <List dense disablePadding>
              <ListItem disableGutters>
                <ListItemText primary="Branch" secondary={`${state.config.branch.name} · ${tz}`} />
              </ListItem>
              <ListItem disableGutters>
                <ListItemText primary="Seeded for" secondary={`${standing.day} (${standing.relative})`} />
              </ListItem>
              <LastResetItem stamp={state.meta.tenantStamp} clock={clock} />
              <ListItem disableGutters>
                <ListItemText primary="Now" secondary={`${clock.dateTime(demoNow)} (branch time)`} />
              </ListItem>
              <ListItem disableGutters>
                {/* A range, not forty dates: the drawer is read at a glance and the
                    list pushed everything below it off the screen (Pete, 2026-09-24). */}
                <ListItemText
                  primary="Real days"
                  secondary={state.meta.realDays.length ? `${state.meta.realDays[0]} – ${state.meta.realDays[state.meta.realDays.length - 1]} (${state.meta.realDays.length} days)` : "none seeded"}
                />
              </ListItem>
              <ListItem disableGutters>
                <ListItemText primary="Tenant" secondary={state.meta.bound ? `bound · ${Object.keys(state.meta.bindings.providerIdsByHandle).length} clinicians · ${state.server.visitResources.length} visits in view` : "not bound — is the tenant seeded and the horizon loaded?"} secondaryTypographyProps={{ color: state.meta.bound ? undefined : "error" }} />
              </ListItem>
              <ListItem disableGutters>
                <ListItemText primary="Demo state" secondary={`${status.holds} open hold(s) · ${status.converted} converted · ${status.rowsOpen} worklist row(s)${status.callOut ? ` · call-out: ${status.callOut}` : ""}`} />
              </ListItem>
              <ListItem disableGutters>
                <ListItemText primary="Seeded" secondary={`${state.meta.seededAt} · schema v${state.meta.schemaVersion}`} />
              </ListItem>
            </List>
          </Box>

          <Divider />

          <ScriptNav />

          <Divider />

          <EventButtons disabled={!state.meta.bound} />

          <Divider />

          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Data
            </Typography>
            <ResetDemoControl timeZone={tz} />
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center" sx={{ mt: 1 }}>
              <Stack direction="row" alignItems="center">
                <Button size="small" variant={confirmReset ? "contained" : "text"} color={confirmReset ? "warning" : "inherit"} onClick={onReset} onBlur={() => setConfirmReset(false)}>
                  {confirmReset ? "Confirm reset" : "Reset this browser"}
                </Button>
                <HelpTip
                  label="Reset this browser"
                  text="This browser only: clears the presenter events, holds and worklist rows on this screen. Nothing on the server changes; whatever the run changed there stays until Reset database."
                />
              </Stack>
              <Button size="small" variant="text" color="inherit" onClick={onDisable}>
                Hide presenter on this browser
              </Button>
            </Stack>
          </Box>

          <Divider />

          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Log
            </Typography>
            {state.presenter.log.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Nothing yet.
              </Typography>
            ) : (
              <List dense disablePadding>
                {[...state.presenter.log].slice(-20).reverse().map((line, i) => (
                  <ListItem key={`${line.atIso}-${i}`} disableGutters>
                    <ListItemText
                      primary={line.text}
                      secondary={clock.stamp(line.atIso)}
                      primaryTypographyProps={{ variant: "body2", color: line.level === "error" ? "error" : undefined }}
                    />
                    {line.level !== "info" && <Chip size="small" label={line.level} color={line.level === "error" ? "error" : "warning"} />}
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        </Box>
      </Drawer>
    </ClickAwayListener>
  );
}
